import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import FavoriteButton from "@/components/base/FavoriteButton";
import ShareBar from "@/components/base/ShareBar";
import SectionHeading from "@/components/base/SectionHeading";
import WorkCard from "@/components/feature/WorkCard";
import TrailerDemo from "@/pages/works/detail/components/TrailerDemo";
import { useSeo } from "@/hooks/useSeo";
import { getWorkById, worksList } from "@/mocks/works";
import { getCharacterById } from "@/mocks/characters";

const infoRows = (work: NonNullable<ReturnType<typeof getWorkById>>) => [
  { label: "作品类型", value: work.type },
  { label: "制作地区", value: work.region },
  { label: "上映年份", value: `${work.year} 年` },
  { label: "集数 / 片长", value: work.episodes },
  { label: "制作公司", value: work.studio },
  { label: "最近更新", value: work.updatedAt },
];

export default function WorkDetailPage() {
  const { id } = useParams();
  const work = getWorkById(id);

  useSeo(
    work ? `${work.title} - 作品介绍 - age动漫` : "作品未找到 - age动漫",
    work
      ? `${work.title}（${work.year}· ${work.type}）：${work.summary} 查看作品简介、角色阵容与相关推荐。`
      : "该作品可能已被移除或链接有误，返回作品分类页浏览更多动漫作品。",
    work ? `${work.title},age动漫,${work.type},${work.tags.join(",")}` : "age动漫,作品未找到",
  );

  if (!work) {
    return (
      <PageShell>
        <div className="w-full px-4 md:px-6 lg:px-10 py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "作品", to: "/works" }, { label: "未找到" }]} />
          <div className="mt-6 max-w-2xl mx-auto">
            <EmptyState
              title="作品内容未找到"
              description="这部作品可能已被移除，或者链接地址有误。可以返回作品分类页，继续浏览其它作品。"
              icon="ri-film-line"
              action={
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Link
                    to="/works"
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回作品分类
                  </Link>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    回到首页
                  </Link>
                </div>
              }
            />
          </div>
        </div>
      </PageShell>
    );
  }

  const characters = work.characterIds
    .map((characterId) => getCharacterById(characterId))
    .filter((character): character is NonNullable<typeof character> => Boolean(character));

  const related = worksList
    .filter((item) => item.id !== work.id)
    .sort((a, b) => {
      const aScore = a.tags.filter((tag) => work.tags.includes(tag)).length + (a.region === work.region ? 1 : 0);
      const bScore = b.tags.filter((tag) => work.tags.includes(tag)).length + (b.region === work.region ? 1 : 0);
      return bScore - aScore;
    })
    .slice(0, 4);

  return (
    <PageShell>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-6 lg:px-10 py-6 md:py-9">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "作品", to: "/works" }, { label: work.title }]} />
          <div className="mt-4 md:mt-5 flex flex-col md:flex-row gap-5 md:gap-7">
            <div className="w-36 sm:w-44 md:w-52 shrink-0 aspect-[4/5] rounded-lg overflow-hidden bg-background-200">
              <img
                src={work.cover}
                alt={`${work.title} 动画海报 - age动漫作品详情`}
                title={`${work.title} - ${work.type}作品介绍`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded bg-accent-500 text-background-50 text-[11px]">{work.type}</span>
                <span className="px-2 py-0.5 rounded bg-background-50 border border-background-200 text-[11px] text-foreground-600">
                  {work.region}
                </span>
                <span className="px-2 py-0.5 rounded bg-background-50 border border-background-200 text-[11px] text-foreground-600">
                  {work.year}
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 leading-tight">{work.title}</h1>
              <p className="mt-1 text-[11px] tracking-[0.16em] text-foreground-400 uppercase">{work.originalTitle}</p>
              <p className="mt-3 text-sm md:text-[15px] text-foreground-600 leading-relaxed max-w-2xl">{work.summary}</p>

              <div className="mt-4 flex flex-wrap gap-2">
                {work.tags.map((tag) => (
                  <span key={tag} className="px-2 py-1 rounded bg-secondary-100 text-secondary-900 text-[11px]">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <FavoriteButton type="works" id={work.id} title={work.title} />
                <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
                  <i className="ri-heart-3-fill text-primary-500"></i>
                  {work.favoriteCount.toLocaleString("zh-CN")} 人收藏
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
                  <i className="ri-movie-2-line text-accent-500"></i>
                  {work.episodes}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-8 lg:gap-10">
          <div className="min-w-0 flex flex-col gap-8 md:gap-10">
            <section aria-labelledby="work-intro-heading">
              <h2 id="work-intro-heading" className="text-base md:text-lg font-semibold text-foreground-950 mb-3">
                作品简介
              </h2>
              <div className="space-y-3.5">
                {work.description.map((paragraph, index) => (
                  <p key={index} className="text-[15px] leading-7 md:leading-8 text-foreground-700">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>

            <section aria-labelledby="work-characters-heading">
              <div className="flex items-end justify-between gap-3 mb-3">
                <h2 id="work-characters-heading" className="text-base md:text-lg font-semibold text-foreground-950">
                  角色列表
                </h2>
                <Link to="/characters" className="text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">
                  全部角色专题
                </Link>
              </div>
              {characters.length === 0 ? (
                <div className="rounded-lg border border-background-200 bg-background-100 p-5 text-sm text-foreground-500">
                  本作暂无角色解析内容，后续会陆续补充。
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {characters.map((character) => (
                    <Link
                      key={character.id}
                      to={`/characters/detail/${character.id}`}
                      className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 hover:border-primary-200 transition-colors cursor-pointer"
                    >
                      <div className="w-14 h-14 rounded-full overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={character.avatar}
                          alt={`${character.name} 角色头像 - ${character.workTitle}`}
                          title={`${character.name} - 角色分析`}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.05]"
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                            {character.name}
                          </h3>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-background-100 text-foreground-500">
                            {character.archetype}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-foreground-500 leading-relaxed line-clamp-2">{character.summary}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            <section aria-labelledby="work-trailer-heading">
              <TrailerDemo
                poster={work.cover}
                title={work.trailerTitle}
                duration={work.trailerDuration}
                workTitle={work.title}
              />
              <h2 id="work-trailer-heading" className="sr-only">作品视频预告</h2>
            </section>
          </div>

          <aside className="lg:sticky lg:top-24 h-fit flex flex-col gap-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="text-sm font-semibold text-foreground-900 mb-3">作品信息</h2>
              <dl className="flex flex-col gap-2.5">
                {infoRows(work).map((row) => (
                  <div key={row.label} className="flex items-start justify-between gap-3 text-xs">
                    <dt className="text-foreground-400 shrink-0">{row.label}</dt>
                    <dd className="text-foreground-800 text-right">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="text-sm font-semibold text-foreground-900 mb-3">收藏与分享</h2>
              <div className="flex flex-wrap gap-2">
                <FavoriteButton type="works" id={work.id} title={work.title} />
              </div>
              <div className="mt-3 pt-3 border-t border-background-100">
                <ShareBar />
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="text-sm font-semibold text-foreground-900 mb-3">相关作品</h2>
              <ul className="flex flex-col gap-3">
                {related.map((item) => (
                  <li key={item.id}>
                    <Link to={`/works/detail/${item.id}`} className="group flex gap-2.5 cursor-pointer">
                      <div className="w-11 h-14 rounded overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={item.cover}
                          alt={`${item.title} 封面`}
                          title={`${item.title} - 作品推荐`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-foreground-800 line-clamp-1 group-hover:text-primary-600 transition-colors">
                          {item.title}
                        </p>
                        <p className="text-[10px] text-foreground-400 mt-0.5">
                          {item.year} · {item.type}
                        </p>
                        <p className="text-[10px] text-foreground-500 mt-1 line-clamp-1">{item.tags.slice(0, 2).join(" / ")}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/works"
                className="mt-4 flex items-center justify-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回作品分类
              </Link>
            </div>
          </aside>
        </div>

        <section className="mt-10 md:mt-14">
          <SectionHeading kicker="RECOMMEND" title="你可能也喜欢" actionLabel="更多作品" actionTo="/works" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {related.map((item) => (
              <WorkCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      </div>
    </PageShell>
  );
}